/**
 * Client for the Copilot /laboklin endpoints.
 *
 * Follows the same shape as copilotApi: ID token bearer against
 * REST_API_ENDPOINT, since these routes sit behind the same Cognito authorizer.
 * Laboklin's own credentials never reach the browser.
 */

import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { getAWSConfig } from '../aws.config';
import type {
  LabReport,
  LabReportSummary,
  LabCatalogue,
  LabOrderPreview,
  ClinicalProblem,
  StructuredWorkup,
} from '../types/laboklin';

export class LaboklinApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public cause?: unknown
  ) {
    super(message);
    this.name = 'LaboklinApiError';
  }
}

class LaboklinApiClient {
  private baseUrl: string;

  constructor() {
    const config = getAWSConfig();
    if (!config.restApiEndpoint) {
      throw new Error('REST_API_ENDPOINT not configured');
    }
    this.baseUrl = config.restApiEndpoint.replace(/\/$/, '');
  }

  private async getAuthToken(): Promise<string> {
    try {
      await getCurrentUser();
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) throw new Error('No ID token available');
      return token;
    } catch (error) {
      throw new LaboklinApiError('Authentication failed', 401, error);
    }
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = await this.getAuthToken();

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options.headers ?? {}),
      },
    });

    if (!response.ok) {
      let message = `Request failed with ${response.status}`;
      try {
        const body = await response.json();
        if (body?.error) message = body.error;
      } catch {
        // Keep the status-based message when the body is not JSON.
      }
      throw new LaboklinApiError(message, response.status);
    }

    return (await response.json()) as T;
  }

  async listReports(since?: string): Promise<LabReportSummary[]> {
    const query = since ? `?since=${encodeURIComponent(since)}` : '';
    const result = await this.request<{ reports: LabReportSummary[] }>(`/laboklin/reports${query}`);
    return result.reports ?? [];
  }

  async getReport(reportNo: string): Promise<LabReport> {
    return this.request<LabReport>(`/laboklin/reports/${encodeURIComponent(reportNo)}`);
  }

  /** Without a problem, returns the category index only; with one, the priced services. */
  async getCatalogue(speciesChar: string, problem?: ClinicalProblem): Promise<LabCatalogue> {
    const params = new URLSearchParams({ species: speciesChar });
    if (problem) params.set('problem', problem);
    return this.request<LabCatalogue>(`/laboklin/catalogue?${params}`);
  }

  /**
   * Re-read a workup that has already run in a conversation, as panels.
   *
   * Safe to call before the diagnostic case exists: the differential comes back
   * empty and `complete` is false, which is how /mylab2 knows to ask again.
   */
  async structureWorkup(payload: {
    conversationId: string;
    reportNo: string;
    caseId?: string;
    signature?: string;
  }): Promise<StructuredWorkup> {
    return this.request<StructuredWorkup>('/laboklin/workup/structure', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /** Prices a prospective order. Places nothing. */
  async previewOrder(payload: {
    services: number[];
    sampleDate?: string;
    dependReport?: string;
    patient?: { name?: string; speciesChar?: string; breedName?: string; sex?: string; dob?: string };
    owner?: { lastname?: string; firstname?: string; country?: string; email?: string };
  }): Promise<LabOrderPreview> {
    return this.request<LabOrderPreview>('/laboklin/order-preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
}

let client: LaboklinApiClient | null = null;

export function getLaboklinApi(): LaboklinApiClient {
  if (!client) client = new LaboklinApiClient();
  return client;
}
